import { Pause, Play } from "lucide-react";
import { forwardRef, useEffect, useRef, useState } from "react";
import { OGVPlayer, OGVLoader } from "ogv";

export interface PlayButtonProps
  extends React.HTMLAttributes<HTMLDivElement> {
  audioUrl: string;
}

OGVLoader.base = "/ogv";

export const PlayButton = forwardRef<HTMLDivElement, PlayButtonProps>(
  ({ audioUrl }, ref) => {
    // can return a function to run when componenet unmounts using useEffect
    // when it unmounts we'll teardown
    useEffect(() => teardown, []);

    const [playing, setPlaying] = useState(false);
    const playerRef = useRef<OGVPlayer | null>(null);

    // unfortunatley doesn't support pausing, just restarting, but it's an audio preview of a couple seconds max 
    // i think this is fine
    const attachListeners = (p: OGVPlayer) => {
      p.addEventListener("ended", teardown);
      p.addEventListener("pause", teardown);
    };

    const detachListeners = (p: OGVPlayer) => {
      p.removeEventListener("ended", teardown);
      p.removeEventListener("pause", teardown);
    };

    const setup = async () => {
      if (playerRef.current) return;

      const p = new OGVPlayer();
      p.src = audioUrl;

      attachListeners(p);
      playerRef.current = p;

      p.play?.();
      setPlaying(true);
    };

    const teardown = () => {
      const p = playerRef.current;
      if (!p) return;

      detachListeners(p);

      p.pause?.();
      p.src = "";
      p.load?.();

      playerRef.current = null;
      setPlaying(false);
    };

    const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
      e.preventDefault();
      playing ? teardown() : setup();
    };

    return (
      <div className="flex items-center gap-2">
        <div
          className="h-[24px] w-[24px] rounded-full bg-musio-blue"
          onClick={handleClick}
          ref={ref}
        >
          {playing ? (
            <Pause className="h-full w-full p-1.5 cursor-pointer" />
          ) : (
            <Play className="h-full w-full p-1.5 cursor-pointer" />
          )}
        </div>
      </div>
    );
  }
);
PlayButton.displayName = "PlayButton";

export default PlayButton;
